import React, { useEffect, useState, useMemo, useCallback } from 'react';
import axios from 'axios';
import EuropeMap from './EuropeMap';
import './IVFJurisdictionTool.css';

const IVFJurisdictionTool = () => {
  // Data states
  const [rawData, setRawData] = useState({
    ivf: null,
    artificial_insemination: null,
    egg_freezing: null
  });
  const [data, setData] = useState([]);
  const [mapColors, setMapColors] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // UI states
  const [age, setAge] = useState('');
  const [methodStatus, setMethodStatus] = useState('');
  const [relationshipStatus, setRelationshipStatus] = useState('');
  const [selectedCountry, setSelectedCountry] = useState(null);
  const [countryData, setCountryData] = useState({});
  const [maxAgeMessage, setMaxAgeMessage] = useState('');

  // List of European countries
  const countries = useMemo(() => [
    "Albania", "Andorra", "Austria", "Belarus", "Belgium", "Bosnia and Herzegovina",
    "Bulgaria", "Croatia", "Cyprus", "Czech Republic", "Denmark", "Estonia", "Finland",
    "France", "Germany", "Greece", "Hungary", "Iceland", "Ireland", "Italy", "Kosovo",
    "Latvia", "Liechtenstein", "Lithuania", "Luxembourg", "Malta", "Moldova", "Monaco",
    "Montenegro", "Netherlands", "North Macedonia", "Norway", "Poland", "Portugal",
    "Romania", "Serbia", "Slovakia",
    "Slovenia", "Spain", "Sweden", "Switzerland", "Turkey (partly in Europe)",
    "Ukraine", "United Kingdom", "Armenia", "Georgia"
  ], []);

  // Memoize the sheet URLs
  const sheetUrls = useMemo(() => ({
    ivf: '/data/IVF-template - ivf.csv',
    artificial_insemination: '/data/IVF-template - artificial_insemination.csv',
    egg_freezing: '/data/IVF-template - egg_freezing.csv'
  }), []);

  // Fetch and cache all data on component mount
  useEffect(() => {
    const fetchAllData = async () => {
      setIsLoading(true);
      setError(null);
      try {
        const requests = Object.entries(sheetUrls).map(([key, url]) => 
          axios.get(url).then(response => [key, response.data])
        );
        
        const responses = await Promise.all(requests);
        const newData = {};
        
        responses.forEach(([key, responseData]) => {
          const parsedData = responseData.split('\n').map(row => row.split(','));
          const headers = parsedData[0];
          const formattedData = parsedData.slice(1).map((row) => {
            return headers.reduce((acc, header, index) => {
              acc[header.trim()] = row[index]?.trim();
              return acc;
            }, {});
          });
          newData[key] = formattedData;
        });
        
        setRawData(newData);
      } catch (error) {
        console.error('Error fetching data:', error);
        setError(error.message);
      } finally {
        setIsLoading(false);
      }
    };

    fetchAllData();
  }, [sheetUrls]);

  // Handlers
  const handleAgeChange = (e) => {
    const inputAge = e.target.value;
    setAge(inputAge);
    setMaxAgeMessage('');
  };
  
  const handleMethodStatusChange = (e) => {
    setMethodStatus(e.target.value);
  };
  
  const handleRelationshipStatusChange = (e) => setRelationshipStatus(e.target.value);

  // Memoize getRelevantColumns to prevent unnecessary recalculations
  const getRelevantColumns = useCallback(() => {
    if (relationshipStatus === "Woman in a heterosexual relationship") {
      return methodStatus === "ivf"
        ? [
            { key: "own_gametes_hetero_couples", label: "Own sperm and eggs" },
            { key: "donated_sperm_hetero_couples", label: "Donated sperm" },
            { key: "donated_eggs_hetero_couples", label: "Donated eggs" },
            { key: "both_hetero_couples", label: "Donated sperm and eggs" },
            { key: "donated_embryos_hetero_couples", label: "Donated embryos" }
          ]
        : methodStatus === "egg_freezing"
          ? [
              { key: "male-female_social_freezing", label: "Social Freezing" },
              { key: "male-female_medical_freezing", label: "Medical Freezing" }
            ]
          : [
              { key: "male_female_own_sperm", label: "Own sperm" },
              { key: "male_female_donated_sperm", label: "Donated sperm" }
            ];
    } else if (relationshipStatus === "Woman in a female relationship") {
      return methodStatus === "ivf"
        ? [
            { key: null, label: "Own sperm and eggs" },
            { key: "donated_sperm_female_couples", label: "Donated sperm" },
            { key: "donated_eggs_female_couples", label: "Donated eggs" },
            { key: "both_female_couples", label: "Donated sperm and eggs" },
            { key: "donated_embryos_female_couples", label: "Donated embryos" }
          ]
        : methodStatus === "egg_freezing"
          ? [
              { key: "female-female_social_freezing", label: "Social Freezing" },
              { key: "female-female_medical_freezing", label: "Medical Freezing" }
            ]
          : [
              { key: null, label: "Own sperm" },
              { key: "female_female_donated_sperm", label: "Donated sperm" }
            ];
    } else if (relationshipStatus === "Single woman") {
      return methodStatus === "ivf"
        ? [
            { key: null, label: "Own sperm and eggs" },
            { key: "donated_sperm_single_women", label: "Donated sperm" },
            { key: "donated_eggs_single_women", label: "Donated eggs" },
            { key: "both_single_women", label: "Donated sperm and eggs" },
            { key: "donated_embryos_single_women", label: "Donated embryos" }
          ]
        : methodStatus === "egg_freezing"
          ? [
              { key: "single_social_freezing", label: "Social Freezing" },
              { key: "single_medical_freezing", label: "Medical Freezing" }
            ]
          : [
              { key: null, label: "Own sperm" },
              { key: "single_donated_sperm", label: "Donated sperm" }
            ];
    }
    return [];
  }, [relationshipStatus, methodStatus]);

  // Memoize calculateColors to prevent unnecessary recalculations
  const calculateColors = useCallback((currentData) => {
    if (!currentData || !age || !methodStatus || !relationshipStatus) return [];
    
    return currentData.map((country) => {
      const [minAge, maxAge] = country['Age Range']?.split('-').map(Number) || [0, 100];
      const isAgeValid = age >= minAge && age <= maxAge;
  
      if (!isAgeValid) {
        return { ...country, color: '#C62828' };
      }
  
      const relevantColumns = getRelevantColumns();
      const values = relevantColumns.map(col => col.key ? country[col.key] : undefined);
      
      const yesCount = values.filter(val => val === "Yes").length;
      const maybeCount = values.filter(val => val === "Maybe").length;
      const validColumns = relevantColumns.filter(col => col.key !== null).length; // Count only valid columns
  
      let color;
  
      if (methodStatus === "ivf") {
        if (relationshipStatus === "Woman in a heterosexual relationship") {
          if (yesCount === relevantColumns.length) {
            color = '#b4d0b4ff';
          } else if (yesCount > 0 || maybeCount > 0) {
            color = '#ffdab9';
          } else {
            color = '#C62828';
          }
        } else {
          // For Single Women and Female Couples
          // Only check the columns that are actually applicable (exclude null key columns)
          if (yesCount === validColumns) {
            color = '#b4d0b4ff';
          } else if (yesCount > 0 || maybeCount > 0) {
            color = '#ffdab9';
          } else {
            color = '#C62828';
          }
        }
      } else if (methodStatus === "artificial_insemination") {
        const isDonatedSpermYes = 
          (relationshipStatus === "Single woman" && country["single_donated_sperm"] === "Yes") ||
          (relationshipStatus === "Woman in a female relationship" && country["female_female_donated_sperm"] === "Yes") ||
          (relationshipStatus === "Woman in a heterosexual relationship" && country["male_female_donated_sperm"] === "Yes");
  
        color = isDonatedSpermYes ? '#b4d0b4ff' : maybeCount > 0 ? '#ffdab9' : '#C62828';
      } else if (methodStatus === "egg_freezing") {
        const isSocialFreezingYes = 
          (relationshipStatus === "Single woman" && country["single_social_freezing"] === "Yes") ||
          (relationshipStatus === "Woman in a female relationship" && country["female-female_social_freezing"] === "Yes") ||
          (relationshipStatus === "Woman in a heterosexual relationship" && country["male-female_social_freezing"] === "Yes");
        
        const isMedicalFreezingYes = 
          (relationshipStatus === "Single woman" && country["single_medical_freezing"] === "Yes") ||
          (relationshipStatus === "Woman in a female relationship" && country["female-female_medical_freezing"] === "Yes") ||
          (relationshipStatus === "Woman in a heterosexual relationship" && country["male-female_medical_freezing"] === "Yes");
        
        if (isSocialFreezingYes && isMedicalFreezingYes) {
          color = '#b4d0b4ff';
        } else if (isSocialFreezingYes || isMedicalFreezingYes || maybeCount > 0) {
          color = '#ffdab9';
        } else {
          color = '#C62828';
        }
      }
  
      return { ...country, color };
    });
  }, [age, methodStatus, relationshipStatus, getRelevantColumns]);

  // Update data and colors when method changes
  useEffect(() => {
    if (!methodStatus || !rawData[methodStatus]) return;
    setData(rawData[methodStatus]);
  }, [methodStatus, rawData]);

  // Calculate colors when data or filters change
  useEffect(() => {
    if (!data.length || !methodStatus || !relationshipStatus || !age) return;
    setMapColors(calculateColors(data));
  }, [data, methodStatus, relationshipStatus, age, calculateColors]);

  // Update country data when selection changes
  useEffect(() => {
    const selectedCountryData = data.find(c => c['Country'] === selectedCountry);
    setCountryData(selectedCountryData || {});
    
    if (selectedCountryData && age) {
      const maxAge = selectedCountryData['Age Range'].split('-')[1];
      if (age > maxAge) {
        setMaxAgeMessage(`Max age for ${displayMethod} in ${selectedCountry} is ${maxAge}.`);
      } else {
        setMaxAgeMessage('');
      }
    } else {
      setMaxAgeMessage('');
    }
  }, [selectedCountry, data, methodStatus, age]);

  // Memoize stats calculations
  const { totalCountries, greenCount, yellowCount, redCount, displayMethod } = useMemo(() => ({
    totalCountries: mapColors.length,
    greenCount: mapColors.filter(country => country.color === '#b4d0b4ff').length,
    yellowCount: mapColors.filter(country => country.color === '#ffdab9').length,
    redCount: mapColors.filter(country => country.color === '#C62828').length,
    displayMethod: methodStatus
      ? methodStatus === "ivf"
        ? "IVF"
        : methodStatus === "egg_freezing"
          ? "Egg Freezing"
          : methodStatus.replace(/_/g, ' ').replace(/\b\w/g, char => char.toUpperCase())
      : ""
  }), [mapColors, methodStatus]);

  if (error) {
    return (
      <div className="error-container">
        <h2>Error Loading Data</h2>
        <p>There was an error loading the fertility data: {error}</p>
        <button onClick={() => window.location.reload()}>Retry</button>
      </div>
    );
  }

  return (
    <div className="container">     
      <div className="unified-search-bar">
        <span class="pc_text">I am a </span>
        <span class="mobile_text first">I am a:</span>
        <select
          value={relationshipStatus}
          onChange={handleRelationshipStatusChange}
          className="search-input"
        >
          <option value="">Select status</option>
          <option value="Woman in a heterosexual relationship">Woman in a heterosexual relationship</option>
          <option value="Woman in a female relationship">Woman in a female relationship</option>
          <option value="Single woman">Single woman</option>
        </select>
        <span class="pc_text">, age </span>
        <span class="mobile_text">Age:</span>
        <input
          type="number"
          value={age}
          onChange={handleAgeChange}
          className="search-input age-input"
          placeholder="0"
        />
        <span class="pc_text">, looking to do </span>
        <span class="mobile_text">Looking for:</span>
        <select
          value={methodStatus}
          onChange={handleMethodStatusChange}
          className="search-input"
        >
          <option value="">Choose method</option>
          <option value="ivf">IVF</option>
          <option value="artificial_insemination">Artificial Insemination</option>
          <option value="egg_freezing">Egg Freezing</option>
        </select>

        <div className="mobile_country_selector">
        <span className="mobile_text">In:</span>
        <select
          value={selectedCountry || ''}
          onChange={(e) => {
            const country = e.target.value;
            setSelectedCountry(country);
          }}
          className="search-input mobile-dropdown"
        >
          <option value="">Select a country</option>
          {countries.length > 0 ? (
            countries.map(country => (
              <option key={country} value={country}>
                {country}
              </option>
            ))
          ) : (
            <option disabled>Loading countries...</option>
          )}
        </select>
      </div>
      </div>
  
      <div className="main-content-wrapper">
        <div className="main-content">
        <EuropeMap 
  data={mapColors} 
  onCountrySelect={setSelectedCountry} 
  selectedCountry={selectedCountry} 
/>
        </div>
  
        <div className="info-wrapper">
        <div className="info-box">
          <p className="text-center inline">
            The law permits you to undergo/access {displayMethod || "(please choose a method)"} in 
            <span className="colored-count inline green">
              {greenCount} <span className="color-square green-square"></span>
            </span> 
            of the {totalCountries} countries,
            but not in 
            <span className="colored-count inline red">
              {redCount} <span className="color-square red-square"></span>
            </span>, 
            and just partially in 
            <span className="colored-count inline yellow">
              {yellowCount} <span className="color-square yellow-square"></span>
            </span>.
          </p>
        </div>

          <div className="country-info-box text-center" key={selectedCountry || 'default'}>
            <p>{selectedCountry ? `${displayMethod} in ${selectedCountry}` : "Select a country"}</p>
            <div className="icon-row">
              {getRelevantColumns().map(({ key, label }) => (
                <div className="icon" key={label}>
                  <svg width="20" height="20">
                    <circle cx="10" cy="10" r="8" fill={
                      !key || !selectedCountry ? '#6a6b6f' // Grey if no country is selected or no data key
                      : countryData[key] === "Yes" ? '#b4d0b4ff' 
                      : countryData[key] === "Maybe" ? '#ffdab9' 
                      : countryData[key] === "No" ? '#C62828' 
                      : '#6a6b6f' // Grey as fallback
                    } />
                  </svg>
                  <span className="icon-label">{label}</span>
                </div>
              ))}
            </div>
            <div className="notes">
              {relationshipStatus === "Woman in a heterosexual relationship" && countryData["male_female_note"] && (
                <p>{countryData["male_female_note"]}</p>
              )}
              {relationshipStatus === "Woman in a female relationship" && countryData["female_female_note"] && (
                <p>{countryData["female_female_note"]}</p>
              )}
              {relationshipStatus === "Single woman" && countryData["single_note"] && (
                <p>{countryData["single_note"]}</p>
              )}

{maxAgeMessage && <p>{maxAgeMessage}</p>} {/* Display max age message */}

            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default IVFJurisdictionTool;
